// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions
// tslint:disable:no-big-function

import {
  IDynamicBindableValue,
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { mergeMultipleConfigs } from '@epicuro-next/platform/dynamic-components-utils';
import { Product, ProductPriceRanges } from 'graphql-api/gql-api-interfaces';

import {
  createInstance,
  createInstanceWithChildren,
} from '../../utils/dynamic-items-utils';
import {
  getItemMediaPreview,
  getProductCardIcons,
  TypeForItemMediaPreviewInput,
} from '../components';
import {} from '../components/item-media-preview';
import { DeliveryDateLabel } from '../components/product-delivery-date-label';
import { productDiscountLabel } from '../components/product-discount-label';
import { ProductSizesPopover } from '../components/product-sizes-popover';
import {
  openConfigStripEventHandler,
  recommendationItemClickedEventHandler,
  recommendationItemShownEventHandler,
} from '../events';
import { LoadableItemView } from '../types/item-view';

import { productHeader } from './product-card-header';
/**
 * Gets a basic view of a product, showing an image, to be passed on the an array renderer for displaying a product.
 * @param propertyToUse The property name that is used by the array renderer to inject data
 * @param eventHandlers The event handlers to apply
 * @returns A set of elements that form the view for the product.
 */
export function getProductAsLoadableImageView(): LoadableItemView {
  const eventHandlers = {
    onClick: [
      recommendationItemClickedEventHandler(
        {
          provider: 'id:root',
          property: 'panelName',
        },
        {
          provider: 'id:root',
          property: 'panelTrackingId',
        },
        {
          provider: 'parent',
          property: 'productData',
        },
      ),
      openConfigStripEventHandler('parent', 'productData'),
    ],
    onVisibleOnce: [
      recommendationItemShownEventHandler(
        {
          provider: 'id:root',
          property: 'panelName',
        },
        {
          provider: 'id:root',
          property: 'panelTrackingId',
        },
        {
          provider: 'parent',
          property: 'productData',
        },
      ),
    ],
  };

  const itemContentType = createInstanceWithChildren({
    id: 'scrollable-item-content',
    contentTypeId: 'dynamic-collections-renderer',
    data: {
      productData: {}, // This will be injected by array renderer
      prices: {
        provider: 'self',
        property: 'productData',
        transform: function (productData: { product: Product }) {
          return productData.product.prices ?? productData.product.priceRanges;
        }.toString(),
      },
      showWholeSalePrices: {
        provider: 'GlobalStoreProviderService',
        property: {
          paths: 'user.showWholeSalePrices',
        },
      },
      price: {
        values: [
          {
            provider: 'self',
            property: 'prices',
          },
          {
            provider: 'self',
            property: 'showWholeSalePrices',
          },
        ],
        transform: function ([prices, showWspdPrice]: [
          ProductPriceRanges[],
          boolean,
        ]) {
          prices = prices ?? [];
          const price =
            window.neoclesDynamicFramework.recommendationsPanels.methods.findPrice(
              prices,
            );

          if (!showWspdPrice) {
            return {
              ...price,
              wspd: null,
            };
          } else {
            return price;
          }
        }.toString(),
      },
    },
    styles: {
      classes: [
        'item-content-type',
        'd-block',
        'position-relative',
        'm-2',
        'rounded',
        'product-card-elements-animation',
      ],
      style: {
        height: 'var(--panel-item-height, 180px)',
        width: 'var(--panel-item-width, 180px)',
        'min-width': 'var(--panel-item-width, 180px)',
        __multiple__: {
          provider: 'id:root',
          property: 'singleItemStyle',
        },
      },
    },
    children: [],
  });

  // TODO @cagin panel: remove m-2
  const loadingSkeletonContentType = createInstance({
    id: 'loading-skeleton-item',
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      style: {
        height: 'var(--panel-item-height, 180px)',
        width: 'var(--panel-item-width, 180px)',
        'min-width': 'var(--panel-item-width, 180px)',
        'border-radius': 'var(--border-radius-lg, .25rem)',
        'background-color': '#f5f5f5',
        __multiple__: {
          provider: 'id:root',
          property: 'singleSkeletonStyle',
        },
      },
      classes: [
        'loading-item-content-type',
        'd-block',
        'm-2',
        'cursor-pointer',
      ],
    },
  });

  // TODO @cagin panel: remove productTitle = let it derive from productHeader
  const productTitle: IDynamicSimpleText = {
    // id: 'square-panel-item-content-product-title', // Does not work ATM as it is not reindexed because it is not the first child in what the array renderer renders.
    contentTypeId: 'dynamic-elements-simple-text',
    inputs: {
      text: {
        provider: 'parent',
        property: 'productData',
        transform: function (val: {
          product: {
            articleTitle?: string;
            masterProductTitle?: string;
            masterProduct: { title: string };
          };
        }) {
          return (
            val.product.masterProduct?.title ??
            val.product.masterProductTitle ??
            val.product.articleTitle
          );
        }.toString(),
      },
    },
    if: ['EQ', [{ provider: 'id:root', property: 'showProductTitle' }, true]],
    styles: {
      classes: [
        'item-content-title',
        'd-block',
        'text-center',
        'px-1',
        'text-truncate',
        'w-100',
        'cursor-pointer',
        'position-absolute',
      ],
      style: {
        'z-index': '3',
        __multiple__: {
          provider: 'id:root',
          property: 'productTitleStyle',
        },
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    eventHandlers: {
      onClick: eventHandlers?.onClick ?? [],
    },
  };
  itemContentType.children.push(productTitle);

  const productPrices = createInstance({
    id: 'product-prices',
    contentTypeId: 'atx-product-prices',
    if: ['EQ', [{ provider: 'id:root', property: 'showProductPrices' }, true]],
    inputs: {
      prices: {
        provider: 'parent',
        property: 'price',
      },
      showWholeSalePrices: {
        provider: 'parent',
        property: 'showWholeSalePrices',
      },
      showRrpPrices: false,
      showWspdPrice: {
        provider: 'ConfigurationsProvider',
        property: {
          namespace: 'pcard',
          featureName: 'show-wspd-price',
          defaultValue: false,
        },
      },
    },
    styles: {
      classes: ['position-absolute'],
      style: {
        bottom: '0',
        right: '2px',
        'z-index': '9',
        width: 'auto',
      },
    },
  });
  itemContentType.children.push(productPrices);

  const [productHeaderItem] = mergeMultipleConfigs(
    {
      ...productHeader,
      if: [
        'EQ',
        [{ provider: 'id:root', property: 'showProductHeader' }, true],
      ],
      data: {
        ...productHeader.data,
        product: {
          provider: 'parent',
          property: 'productData',
          transform: function (response: { product: Product }) {
            return response.product;
          }.toString(),
        },
      },
    },
    [
      {
        id: 'product-header-style-id',
        contentTypeId: 'atx-product-card-style-id',
        if: ['EQ', [false, true]],
      },
      {
        id: 'product-header-prices',
        contentTypeId: 'atx-product-prices',
        styles: {
          classes: ['mt-3', 'justify-content-end'],
        },
      },
    ],
  );

  itemContentType.children.push(productHeaderItem as IDynamicItem);

  const mediaProvider: IDynamicBindableValue = {
    provider: 'parent',
    property: 'productData',
    transform: function (productDataArg: TypeForItemMediaPreviewInput) {
      return productDataArg.media;
    }.toString(),
  };

  const itemMediaPreview = getItemMediaPreview(
    'item-media-preview',
    mediaProvider,
    eventHandlers,
    {
      classes: ['content-type-media-preview', 'cursor-pointer'],
    },
  );
  itemContentType.children.push(itemMediaPreview);

  const productCardIcons = getProductCardIcons();
  itemContentType.children.push(productCardIcons);

  const productCardDiscountLabel: IDynamicItem = productDiscountLabel;
  itemContentType.children.push(productCardDiscountLabel);

  const productSizesPopover = ProductSizesPopover;
  itemContentType.children.push(productSizesPopover);

  const deliveryDateLabel = DeliveryDateLabel;
  itemContentType.children.push(deliveryDateLabel);

  const loadableItemView: LoadableItemView = {
    itemContentType,
    loadingSkeletonContentType,
  };

  return loadableItemView;
}
